import { NavigationFailure, NavigationGuardNext, RouteLocationNormalized, Router } from 'vue-router';
import { FeatureFlag, isEnabled } from '@monolith/legacy/services/features';
import Store from '@monolith/legacy/store';
import { captureException } from '@core/plugins/sentry';
import {
  getEmailCampaign,
  getPaginatedEmailCampaigns,
  getPaginatedEmailRecipients,
} from '@monolith/legacy/services/api/email-campaigns';
import { getIdFromSlug } from '@core/utilities/slug';
import BrandTracking from '@monolith/legacy/services/brand-tracking';
import loadCarts from '@core/routes/middleware/load-carts';
import i18n, { loadLanguageAsync } from '@monolith/legacy/services/i18n';
import { SaveBarMode } from '@monolith/legacy/store/account/types';
import Analytics from '@monolith/legacy/services/analytics';
import { syncLanguageSwitcherUrls } from '@core/utilities/sync-language-switcher';
import { FETCH_OFFERS_AND_EVENTS } from '@monolith/legacy/store/offers/action-types';
import { FETCH_ORDERPAY_ACCOUNT_INFO } from '@client/monolith/legacy/store/account/orderpay/action-types';

export async function orderListGuard(_to, _from, next: NavigationGuardNext) {
  if (Store.getters.isUserAuthenticated && !Store.getters.userIsRetailer) {
    try {
      await Store.dispatch('account/publicOrdersIntegration/getActiveIntegration');
    } catch (e) {
      captureException(e);
    }
  }
  next();
}

export function preparedOrderGuard(_to, _from, next: NavigationGuardNext) {
  if (isEnabled('prepared_orders')) {
    next();
  } else {
    next({ path: '/account/orders' });
  }
}

// `/account/smart-reorder` is a flag-gated, smart-only view served by the
// Smart Reorder page. Non-flagged visitors are redirected to the legacy
// `/account/reorder` route so the URL never dead-ends.
export function smartReorderGuard(_to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) {
  if (isEnabled('smart_reorder_enabled')) {
    next();
  } else {
    next({ name: 'account-reorder' });
  }
}

export function editPreparedOrderGuard(_to, _from, next: NavigationGuardNext) {
  if (isEnabled('save_draft_new_order')) {
    next();
  } else {
    next({ name: 'account.prepared-orders.preview' });
  }
}

export async function accountIntegrationsGuard(to, _from, next: NavigationGuardNext) {
  if (to?.query?.tab === 'my-network' && isEnabled(FeatureFlag.DisableWaxIntegration)) {
    next({ name: 'integrations-listing' });
    return;
  }

  await Store.dispatch('account/publicOrdersIntegration/getEnabledIntegrations');
  await Store.dispatch('account/publicOrdersIntegration/getActiveIntegration');
  next();
}

export async function campaignGuard(to, _from, next: NavigationGuardNext) {
  const currentPage = parseInt(<string>to.query.page ?? '1');
  const data = await getPaginatedEmailCampaigns(currentPage);
  // vue-router Route type expects all params to be strings but when passing more
  // complex params as props this is no longer the case.
  to.params.campaigns = <string>(<unknown>data);
  next();
}

export async function campaignDetailGuard(to, _from, next: NavigationGuardNext) {
  const campaign = await getEmailCampaign(parseInt(to.params.id));
  to.params.campaign = <string>(<unknown>campaign);
  next();
}

export async function campaignRecipientsGuard(to, _from, next: NavigationGuardNext) {
  const recipients = await getPaginatedEmailRecipients(Number(to.params.id), Number(to.query.page));
  // vue-router Route type expects all params to be strings but when passing more
  // complex params as props this is no longer the case.
  to.params.recipients = <string>(<unknown>recipients);
  next();
}

export async function accountSubscriptionGuard(
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const program = Store.getters['offers/ankorstorePlus'];

  if (!program?.optIn) {
    next('/connected');
  } else {
    next();
  }
}

export async function brandCollectionGuard(to, _from, next: NavigationGuardNext) {
  const brandId = getIdFromSlug(to.params.brand);
  const brand = Store.getters['shop/brandById'](brandId);
  if (!brand?.isRefreshed) {
    await Store.dispatch('shop/fetchBrand', {
      brand_id: brandId,
    });
  }
  return next();
}

export async function brandDetailGuard(to, _from, next: NavigationGuardNext) {
  const brandId = parseInt(to.params.brand.split('-').slice(-1)[0], 10);
  const brand = Store.getters['shop/brandById'](brandId);

  if (!brand?.isRefreshed) {
    try {
      await Store.dispatch('shop/fetchBrand', {
        brand_id: brandId,
      });
    } catch (err) {
      // In case error with the parser
      return next({ name: 'PageNotFound' });
    }
  }

  await BrandTracking.track(brandId, null);

  //prevent browsing invalid page number

  if (!to.query?.p) {
    return next();
  }

  const page = Number(to.query?.p);
  if (page === 1 || Number.isNaN(page)) {
    const { p, ...query } = to.query;
    return next(Object.assign({}, to, { query }));
  }
  return next();
}

export async function productDetailGuard(to, _from, next: NavigationGuardNext) {

  const brandId = parseInt(to.params.brand.split('-').slice(-1)[0], 10);
  const productId = parseInt(to.params.product.split('-').slice(-1)[0], 10);

  try {
    BrandTracking.track(brandId, productId);
    await Store.dispatch('fetchProduct', {
      product_id: productId,
    });
    next();
  } catch (err) {
    // In case error with the parser
    return next({ name: 'PageNotFound' });
  }
}

export async function myListGuard(_to, _from, next: NavigationGuardNext) {
  await Promise.all([Store.dispatch('wishlist/markTooltipAsSeen'), Store.dispatch('wishlist/resetRetailerProductLikeCount')]);
  next();
}

export async function globalBeforeEachGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, router: Router) {
  if (!Store.state?.offers?.events.length) {
    await Store.dispatch('offers/' + FETCH_OFFERS_AND_EVENTS);
  }
  loadCarts(to);
  const lang = document.documentElement.lang;
  if (lang) {
    await loadLanguageAsync(lang, i18n);

    if (!router.app) {
      return false;
    }

    if (
      !isEnabled('activate_router_from_account_to_marketplace') &&
      from.matched[0]?.path === '/account' &&
      to.matched[0]?.path !== '/account'
    ) {
      window.location.href = window.location.origin + to.fullPath;
      return false;
    }
    if (
      Store.getters['account/getSaveBarMode'] !== SaveBarMode.Disabled &&
      Store.getters['account/isDiscardChangesNotificationActive']
    ) {
      Store.dispatch('account/displayDiscardChanges', to);
      if (Store.getters['account/isPopinDisplayed']) {
        Store.dispatch('account/changeSaveBarMode', SaveBarMode.Disabled);
        return true;
      } else {
        return false;
      }
    } else {
      Store.dispatch('account/changeSaveBarMode', SaveBarMode.Disabled);
      return true;
    }
  } else {
    return false;
  }
}

export function globalAfterEachGuard(to, from, failure?: NavigationFailure | void) {
  // Neither of these is a page view, so neither should reach Segment:
  // - a hash-only change on the same path is a catalog filter/sort/pagination interaction
  //   (the catalog writes its search state to the hash: #sortBy=, #max-discount-rate=)
  // - a failed navigation never landed anywhere
  // The language-switcher sync below still runs in both cases — the URL did change.
  const isHashOnlyChange = to.path === from?.path && to.hash !== from?.hash;

  if (!failure && !isHashOnlyChange) {
    Analytics.page({ to, from });
  }

  if (from?.name) {
    syncLanguageSwitcherUrls(to.path, Store.getters.isUserAuthenticated);
  }
}

export async function landingSubscriptionProgramGuard(_to, _from, next: NavigationGuardNext) {
  const offerProgram = Store.getters['offers/ankorstorePlus'];

  if (Store.getters.userIsRetailer && !offerProgram) {
    next('/connected');
  } else if (offerProgram?.optIn) {
    next({ name: 'ankorstore-plus' });
  } else {
    next();
  }
}

export async function isLoggedInGuard(_from, _to, next) {
  const isUserAuthenticated = Store.getters['isUserAuthenticated'];

  if (!isUserAuthenticated) {
    return next({ path: '/login' });
  }

  const brand = Store.getters['brand'];

  if (brand?.orderpay?.eligible) {
    await Store.dispatch('account/orderpay/' + FETCH_ORDERPAY_ACCOUNT_INFO, {
      userUuid: brand.uuid,
    });
  }

  next();
}

export async function accountFulfillmentGuard(_to, _from, next: NavigationGuardNext) {
  if (isEnabled('ff-spa')) {
    window.location.href = '/v2/account/fulfilment/replenishments';
  } else {
    next();
  }
}

export async function onBoardingFlowV2Guard(to, _from, next: NavigationGuardNext) {

  const mapUrl = {
    brandSettingsCompanyDetails: '/v2/account/settings/onboarding/company-details',
    brandSettingsShipping: '/v2/account/settings/onboarding/shipping',
  };

  if (Store.getters['isPartiallyCompleted']) {
    window.location.href = `/v2${to.path}`;
    return false;
  }
  if (Store.getters['userIsCandidateBrand']) {
    const url = mapUrl[to.name]
    window.location.href = url;
    return false;
  }
  return next();
}

export async function paymentsGuard(_to, _from, next: NavigationGuardNext) {
  if (!isEnabled(FeatureFlag.DisableBrandFinanceEmailBanner) && !Store.getters['userIsRetailer']) {
    await Store.dispatch('account/billing/fetchFinanceContact');
  }
  next();
}

export async function retailerOnlyGuard(_to, _from, next: NavigationGuardNext) {
  const isUserAuthenticated = Store.getters['isUserAuthenticated'];
  const userIsRetailer = Store.getters['userIsRetailer'];

  if (!isUserAuthenticated) {
    return next({ path: '/login' });
  }

  if (!userIsRetailer) {
    return next({ path: '/' });
  }

  next();
}

export function changelogGuard(_to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext): void {
  if (isEnabled(FeatureFlag.DisableWhatsNewPage)) {
    next({ path: '/' });
  } else {
    next();
  }
}
